import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { evolve, mergeLeft, pipe } from 'ramda'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import toInteger from 'lodash/toInteger'

import { reportProblem } from '@/ports'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import Yup from '@/lib/yup'
import { callOr } from '@/utils'
import { Button } from '@/components/button-new'
import { FieldDropdown, FieldTextarea, Group } from '@/components/form'
import { ModalTitle } from '@/components/modal'
import { createModal, useModalize } from '@/components/modalize'
import { toast } from '@/components/toast-notifications'

const Wrapper = styled.div`
  disaply: flex;
  flex-direction: column;
  height: 100%;
`

const StyledForm = styled(Form)`
  disaply: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`

const Title = styled(ModalTitle)`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  text-align: left;
  width: 100%;
  padding: 24px;
  border-bottom: 1px solid ${colors.grey88};
  margin-bottom: 24px;
`

const StyledGroup = styled(Group)`
  padding: 0px 24px;
  margin-bottom: 24px;
`

const ButtonWrapper = styled.div`
  display: flex;
  padding: 24px;
  justify-content: flex-end;
  background: ${colors.grey96};
  border-top: 1px solid ${colors.grey88};
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
  }
`

const StyledButton = styled(Button)`
  width: 120px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

type ReportData = {
  listingId: number
  buildingId: number
  targetUserId: number
}

interface Props {
  data: ReportData
  reporterUserId: number
  initialReportType?: string
}

type Form = {
  user_message: string
  reference_type: string
}

const ReportProblemModal: React.FC<Props> = ({
  data,
  reporterUserId,
  initialReportType = 'listing'
}) => {
  const { close } = useModalize()
  const { t } = useTranslation()

  const validationSchema = Yup.object().shape({
    user_message: Yup.string().min(1).max(500).required(),
    reference_type: Yup.string().required()
  })

  const initialValues = {
    user_message: '',
    reference_type: initialReportType
  }

  const MAP: Record<string, number> = {
    listing: data.listingId,
    building: data.buildingId,
    user: data.targetUserId
  }

  const onSubmit = async (
    values: Form,
    { setSubmitting }: FormikHelpers<Form>
  ) => {
    const reference_id = toInteger(MAP[values.reference_type])

    const body = pipe(
      mergeLeft({
        reference_id,
        user_id: reporterUserId
      }),
      evolve({
        user_id: callOr(toInteger, null),
        reference_id: callOr(toInteger, null)
      })
    )(values)

    const { response } = await reportProblem({ body })

    setSubmitting(false)

    if (response.ok) {
      close()
    } else {
      toast(
        t(
          'listing:b.listing.view.report_modal.error.text',
          'Oops! Something went wrong while sending your report. Please try again.'
        ),
        {
          title: t(
            'listing:b.listing.view.report_modal.error.title',
            'Listing Report Failed'
          ),
          type: 'error'
        }
      )
    }
  }

  return (
    <Wrapper>
      <Title>
        {t(
          'listing:b.listing.view.report_listing_modal.title',
          'Report this listing'
        )}
      </Title>
      <Formik
        padding="15px"
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ isSubmitting }: FormikProps<Form>) => (
          <StyledForm>
            <StyledGroup>
              <FieldDropdown
                name="reference_type"
                defaultValue={{
                  value: 'listing',
                  label: t(
                    'listing:b.listing.view.report_modal.dropdown.listing.text',
                    'Listing'
                  )
                }}
                placeholder={t(
                  'listing:b.listing.view.report_modal.related.text',
                  'Choose a category'
                )}
                required
                options={[
                  {
                    value: 'listing',
                    label: t(
                      'listing:b.listing.view.report_modal.dropdown.listing.text',
                      'Listing'
                    )
                  },
                  {
                    value: 'user',
                    label: t(
                      'listing:b.listing.view.report_modal.dropdown.user.text',
                      'User'
                    )
                  },
                  {
                    value: 'building',
                    label: t(
                      'listing:b.listing.view.report_modal.dropdown.building.text',
                      'Building'
                    )
                  },
                  {
                    value: 'other',
                    label: t(
                      'listing:b.listing.view.report_modal.dropdown.other.text',
                      'Other'
                    )
                  }
                ]}
              />
            </StyledGroup>
            <StyledGroup>
              <FieldTextarea
                name="user_message"
                placeholder={t(
                  'listing:b.listing.view.report_modal.description.label',
                  'Please describe the problem with this listing.'
                )}
                maxLength={500}
                height={200}
              />
            </StyledGroup>
            <ButtonWrapper>
              <StyledButton
                theme="primary"
                type="submit"
                disabled={isSubmitting}
                size={'unset'}
              >
                {t(
                  'listing:b.listing.view.report_modal.submit.button',
                  'Submit'
                )}
              </StyledButton>
            </ButtonWrapper>
          </StyledForm>
        )}
      </Formik>
    </Wrapper>
  )
}

export const show = createModal(ReportProblemModal, {
  maxWidth: 640,
  width: '100%'
})
